import {rootState} from '@mintdata-wsp/common/state/RootState';
import {CommandType, ICommandMenuItem} from '@mintdata-wsp/common/state/ui/visualDesigner/CommandMenuState';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {Page, PageId} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {
    PopupDismissedReason,
    PopupDismissedReasons
} from '@mintdata/mint-kit/impl/components/popover/PopupDismissedReason';
import bind from 'bind-decorator';
import {action} from 'mobx';

export class CommandMenuController
{
    public static KEY_MAP =
        {
            // _esc: 'esc',
            _confirm: 'enter',
            _selectPrevItem: 'up',
            _selectNextItem: 'down',
            _toggleCommandMenu: 'mod+shift+f'
        };

    public static readonly KEY_HANDLERS = {};

    @bind
    public init():void
    {
        this._initKeyHandlersMap();
    }

    @bind
    public getKeyMap():any
    {
        return CommandMenuController.KEY_MAP;
    }

    @bind
    public getKeyHandlers():any
    {
        return CommandMenuController.KEY_HANDLERS;
    }

    private _initKeyHandlersMap()
    {
        const keyHandlers = CommandMenuController.KEY_HANDLERS;
        const keyMap = CommandMenuController.KEY_MAP;

        for( const funcName in keyMap )
        {
            const keyHandler = this[funcName];

            if( !keyHandler )
                console.error(`Could not find keyboard handler for ${funcName}`);

            keyHandlers[funcName] = keyHandler;
        }
    }

    @action.bound
    filterItems(filterText:string)
    {
        const {ui: {visualDesigner: {commandMenuState}}} = rootState;

        commandMenuState.filterText = filterText;
        commandMenuState.selectedItemIndex = 0;
    }

    @action.bound
    onItemClick(item:ICommandMenuItem, index:number)
    {
        const {ui: {visualDesigner: {commandMenuState}}} = rootState;
        const {ui: {surfaceArea, spreadsheets: {main, tabs, meshSelection, workbook, mesh}}} = controllers;

        if( item )
        {
            if( item.type === CommandType.SHEET )
            {
                const sheetId:SheetId = item.data.sheetId;
                const colIndexFrom:number = item.data.colIndexFrom;
                const rowIndexFrom:number = item.data.rowIndexFrom;
                const colIndexTo:number = item.data.colIndexTo;
                const rowIndexTo:number = item.data.rowIndexTo;
                const isRange:boolean = colIndexTo != null || rowIndexTo != null;

                const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(sheetId);
                const sheet:AnySheet = main.findAnySheetById(sheetId);

                commandMenuState.isMenuOpen = false;

                setTimeout(() =>
                {
                    tabs.setActiveSheet(sheet.workbook.id, sheetId);

                    if( isRange )
                        meshSelection.selectCellRange(ctx, new MeshLocation(colIndexFrom, rowIndexFrom), new MeshLocation(colIndexTo, rowIndexTo));
                    else
                        meshSelection.selectCell(ctx, new MeshLocation(colIndexFrom, rowIndexFrom));

                    mesh.focusMesh(ctx);
                });
            }
            else if( item.type === CommandType.PAGE )
            {
                const pageId:PageId = item.data.pageId;

                commandMenuState.isMenuOpen = false;

                setTimeout(() =>
                {
                    const page:Page = surfaceArea.findPageById(pageId);

                    if( page )
                        surfaceArea.setActivePage(page);
                });
            }
        }
    }

    @action.bound
    toggleCommandMenuVisibility()
    {
        const {ui: {visualDesigner: {commandMenuState}}} = rootState;

        if( !commandMenuState.isMenuOpen )
        {
            commandMenuState.prevFocusedElement = document.activeElement as HTMLElement;

            commandMenuState.isMenuOpen = true;
            commandMenuState.filterText = null;
            commandMenuState.selectedItemIndex = 0;
        }
        else
            commandMenuState.isMenuOpen = false;
    }

    @action.bound
    onDismissed(reason:PopupDismissedReason)
    {
        const {ui: {visualDesigner: {commandMenuState}}} = rootState;

        if( reason && reason.reason === PopupDismissedReasons.Keyboard && commandMenuState.prevFocusedElement )
            commandMenuState.prevFocusedElement.focus();

        commandMenuState.isMenuOpen = false;
    }

    @action.bound
    _toggleCommandMenu(event:KeyboardEvent)
    {
        const {ui: {visualDesigner: {commandMenuState}}} = rootState;

        this._esc(event);
    }

    @action.bound
    _esc(event:KeyboardEvent)
    {
        const {ui: {visualDesigner: {commandMenuState}}} = rootState;

        commandMenuState.isMenuOpen = false;

        if( commandMenuState.prevFocusedElement )
            commandMenuState.prevFocusedElement.focus();

        event.stopPropagation();
        event.preventDefault();
    }

    @action.bound
    _confirm(event:KeyboardEvent)
    {
        const {ui: {visualDesigner: {commandMenuState: {selectedItem, selectedItemIndex}}}} = rootState;

        this.onItemClick(selectedItem, selectedItemIndex);

        event.stopPropagation();
        event.preventDefault();
    }

    @action.bound
    _selectPrevItem(event:KeyboardEvent)
    {
        const {ui: {visualDesigner: {commandMenuState}}} = rootState;

        commandMenuState.selectedItemIndex--;

        if( commandMenuState.selectedItemIndex < 0 )
            commandMenuState.selectedItemIndex = commandMenuState.filteredItems.length - 1;

        event.stopPropagation();
        event.preventDefault();
    }

    @action.bound
    _selectNextItem(event:KeyboardEvent)
    {
        const {ui: {visualDesigner: {commandMenuState}}} = rootState;

        commandMenuState.selectedItemIndex++;

        if( commandMenuState.selectedItemIndex >= commandMenuState.filteredItems.length )
            commandMenuState.selectedItemIndex = 0;

        event.stopPropagation();
        event.preventDefault();
    }
}
